import React from 'react';
import { NextPage } from 'next';
import { connect } from 'react-redux';

import styled from 'styled-components';
import api from 'api/expertise';
import rootActions from 'redux/rootActions';
import { Expertises } from 'types/expertises';
import { GetInitialProps } from 'types/getInitialProps';
import { fromLarge } from 'styleUtils/mediaQueries';
import { isIndexEven } from 'utils/math';
import { gridDisplay, gridTemplateColumns, gridTemplateRows, gridRow } from 'styleUtils/grid';

import Markdown from 'markdown-to-jsx';
import { FlexContainer } from 'components/common/FlexContainer';
import { Layout } from 'components/common/Layout';
import { MeetTheExpertise } from 'components/expertise/MeetTheExpertise';
import { MetaTags } from 'components/meta/MetaTags';
import { PageIntroduction } from 'components/page/PageIntroduction';
import { PageSectionIntroLimiter } from 'components/page/PageSectionIntroLimiter';
import { Section } from 'components/common/Section';
import { TransparentContainer } from 'components/common/TransparentContainer';

const SectionChildrenWrapper = styled.div`
    ${gridDisplay()};
    ${gridTemplateColumns('100%')}
    ${gridTemplateRows('auto')}

    ${fromLarge`
        ${gridTemplateColumns('100%')}
        ${gridTemplateRows('auto auto')}
  `};
`;

const MeetTheExpertiseWrapper = styled.div`
    ${gridRow({ start: 2 })}

    margin: 64px 0;
`;

interface Props {
    data: {
        introduction: string;
        expertises: Expertises[];
    };
    pathName: string;
    type: string;
}

const Expertise: NextPage<Props> = (props) => {
    const {
        data: { introduction, expertises },
        pathName,
        type = 'expertise',
    } = props;

    return (
        <Layout pathName={pathName}>
            <MetaTags
                title="Werken bij Developers.nl | Expertise"
                image="/favicons/logo_180.png"
                description="Developers.nl is verdeeld in vier kenniscellen; PHP, Frontend, Java en Python. Op deze manier zit je dus altijd op je plaats."
                keywords="developer, developers, frontend, php, python, java, learn, code, share"
            />
            {props.data && (
                <div>
                    <PageIntroduction description={introduction} />
                    {expertises.map((expertise, index) => (
                        <Section isEven={isIndexEven(index)} hasWhiteSpace key={index}>
                            <FlexContainer>
                                <TransparentContainer>
                                    <SectionChildrenWrapper>
                                        <PageSectionIntroLimiter>
                                            <Markdown>{expertise.description}</Markdown>
                                        </PageSectionIntroLimiter>
                                        <MeetTheExpertiseWrapper>
                                            <MeetTheExpertise
                                                type={type}
                                                letsMeet={expertise.letsMeet}
                                                highlight={expertise.profile}
                                                offsetMeet={expertise.offsetMeet}
                                                meetRight={expertise.meetRight}
                                                image={expertise.thumbnail}
                                            />
                                        </MeetTheExpertiseWrapper>
                                    </SectionChildrenWrapper>
                                </TransparentContainer>
                            </FlexContainer>
                        </Section>
                    ))}
                </div>
            )}
        </Layout>
    );
};

Expertise.getInitialProps = async (ctx: GetInitialProps): Promise<any> => {
    const { store } = ctx;

    if (store.getState().expertise.data) {
        return {
            pathName: ctx.pathname,
        };
    }

    return api.data().then(
        (payload) => {
            store.dispatch(rootActions.fetchExpertiseSuccess(payload));

            return {
                pathName: ctx.pathname,
            };
        },
        (response) => {
            const error = response.error || {
                message: response,
                code: 404,
            };

            store.dispatch(rootActions.fetchExpertiseError(error));

            return {
                pathName: ctx.pathname,
            };
        },
    );
};

export default connect((state) => {
    return {
        data: state.expertise.data,
    };
}, rootActions)(Expertise);
